import { useState, useRef } from 'react';

interface WeekHeatmapChartProps {
  cells: Array<{ day: number; hour: number; count: number }>;
  // day: 1=Mon … 5=Fri (PostgreSQL DOW convention)
  // hour: 0–23
}

const DAY_LABELS: Record<number, string> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 0: 'Sun' };
const DISPLAY_HOURS = Array.from({ length: 24 }, (_, i) => i); // 0–23

function fmtHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

// Show axis labels every 3 hours (24h clock)
const HOUR_AXIS_LABELS: Record<number, string> = {
  0: '0',
  3: '3',
  6: '6',
  9: '9',
  12: '12',
  15: '15',
  18: '18',
  21: '21',
};

interface TooltipState {
  day: number;
  hour: number;
  count: number;
  x: number;
  y: number;
}

export function WeekHeatmapChart({ cells }: WeekHeatmapChartProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const cellMap = new Map(cells.map((c) => [`${c.day}-${c.hour}`, c.count]));
  const maxCount = Math.max(...cells.map((c) => c.count), 1);

  const cellW = 14;
  const cellH = 16;
  const labelW = 30;
  const labelH = 16;
  const gap = 2;
  const totalW = labelW + DISPLAY_HOURS.length * (cellW + gap);
  const DAYS_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon–Fri, then Sat, Sun
  const totalH = labelH + DAYS_ORDER.length * (cellH + gap);

  function handleMouseEnter(e: React.MouseEvent<SVGRectElement>, day: number, hour: number, count: number) {
    const rect = e.currentTarget.getBoundingClientRect();
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect) return;
    setTooltip({
      day,
      hour,
      count,
      x: rect.left - svgRect.left + rect.width / 2,
      y: rect.top - svgRect.top,
    });
  }

  return (
    <div className="relative select-none" style={{ maxWidth: Math.round(totalW * 1.3) }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${totalW} ${totalH}`}
        className="w-full"
        role="img"
        aria-label="R&D work pattern by day and hour"
        onMouseLeave={() => setTooltip(null)}
      >
        {/* Hour axis labels — every 3 hours */}
        {DISPLAY_HOURS.map((hour, i) =>
          HOUR_AXIS_LABELS[hour] ? (
            <text
              key={hour}
              x={labelW + i * (cellW + gap) + cellW / 2}
              y={labelH - 3}
              textAnchor="middle"
              fontSize={7}
              className="fill-muted-foreground"
            >
              {HOUR_AXIS_LABELS[hour]}
            </text>
          ) : null,
        )}

        {/* Day rows */}
        {DAYS_ORDER.map((day, di) => (
          <g key={day}>
            <text
              x={labelW - 4}
              y={labelH + di * (cellH + gap) + cellH / 2 + 4}
              textAnchor="end"
              fontSize={8}
              className="fill-muted-foreground"
            >
              {DAY_LABELS[day]}
            </text>
            {DISPLAY_HOURS.map((hour, hi) => {
              const count = cellMap.get(`${day}-${hour}`) ?? 0;
              const intensity = count / maxCount;
              const isHovered = tooltip?.day === day && tooltip?.hour === hour;
              return (
                <rect
                  key={hour}
                  x={labelW + hi * (cellW + gap)}
                  y={labelH + di * (cellH + gap)}
                  width={cellW}
                  height={cellH}
                  fill={
                    count === 0
                      ? `oklch(0.93 0.005 250)`
                      : `oklch(${0.65 - intensity * 0.25} ${0.05 + intensity * 0.15} 250)`
                  }
                  stroke={isHovered ? 'oklch(0.45 0.15 250)' : 'none'}
                  strokeWidth={isHovered ? 1 : 0}
                  rx={2}
                  style={{ cursor: count > 0 ? 'pointer' : 'default' }}
                  onMouseEnter={(e) => handleMouseEnter(e, day, hour, count)}
                  aria-label={`${DAY_LABELS[day]} ${fmtHour(hour)}: ${count} session${count !== 1 ? 's' : ''}`}
                />
              );
            })}
          </g>
        ))}
      </svg>

      {/* Floating tooltip */}
      {tooltip && (
        <div
          className="pointer-events-none absolute z-10 rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md"
          style={{
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: 'translate(-50%, -100%)',
            whiteSpace: 'nowrap',
          }}
        >
          <span className="font-medium">{DAY_LABELS[tooltip.day]} {fmtHour(tooltip.hour)}</span>
          <span className="text-muted-foreground ml-1.5">
            {tooltip.count} session{tooltip.count !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
}
